import { getFormProps, useForm } from "@conform-to/react"
import { getZodConstraint, parseWithZod } from "@conform-to/zod"
import * as E from "@react-email/components"
import { json, redirect, type ActionFunctionArgs } from "@remix-run/node"
import { useActionData } from "@remix-run/react"
import { HoneypotInputs } from "remix-utils/honeypot/react"
import { z } from "zod"
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx"
import {
	Form,
	FormContext,
	FormControl,
	FormErrorList,
	FormField,
	FormLabel,
	FormMessage,
} from "#app/components/ui/forms/conform-integrated/form"
import { Input } from "#app/components/ui/forms/conform-integrated/input"
import { StatusButton } from "#app/components/ui/status-button.tsx"
import { TypographyH1 } from "#app/components/ui/typography.tsx"
import { APP_NAME } from "#app/utils/constants.ts"
import { prisma } from "#app/utils/db.server.ts"
import { sendEmail } from "#app/utils/email.server.ts"
import { checkHoneypot } from "#app/utils/honeypot.server.ts"
import { generateMeta } from "#app/utils/meta.js"
import { useIsPending } from "#app/utils/misc.tsx"
import { EmailSchema } from "#app/utils/user-validation.ts"
import routes from "#routes/index.js"
import { prepareVerification } from "./verify.server"

const SignupSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ["email"],
					code: z.ZodIssueCode.custom,
					message: "Ya existe un usuario con este correo",
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== "success") {
		return json(
			{ result: submission.reply() },
			{
				status: submission.status === "error" ? 400 : 200,
			},
		)
	}

	const { email } = submission.value
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: "onboarding",
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: `Bienvenido a ${APP_NAME}!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === "success") {
		return redirect(redirectTo.toString())
	} else {
		return json(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="es" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Bienvenido a {APP_NAME}!</E.Text>
				</h1>
				<p>
					<E.Text>
						Aquí tienes el código de verificación: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>O puedes hacer click aquí:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta = generateMeta({
	title: `Registro | ${APP_NAME}`,
	description: `Regístrate para acceder a una experiencia mejorada de compra.`,
	canonical: routes.public.auth_register.route(),
})

export default function SignupRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	// const [searchParams] = useSearchParams()
	// const redirectTo = searchParams.get("redirectTo")

	const [form, fields] = useForm({
		id: "signup-form",
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: "onBlur",
	})

	return (
		<div className="mx-auto flex min-h-full max-w-sm flex-col justify-center">
			<div className="flex flex-col gap-4 text-center">
				<TypographyH1>Únete a nosotros</TypographyH1>
				<p className="mb-8 text-center text-base text-gray-700">
					Regístrate para acceder a una experiencia mejorada de compra.
				</p>
			</div>
			<div className="mx-auto flex min-w-full flex-col gap-4">
				<FormContext context={form.context}>
					<Form
						method="POST"
						{...getFormProps(form)}
						className="flex flex-col gap-4"
					>
						<HoneypotInputs />
						<FormField field={fields.email}>
							<FormLabel>Correo electrónico</FormLabel>
							<FormControl>
								<Input
									type="email"
									autoFocus
									autoComplete="email"
									meta={fields.email}
								/>
							</FormControl>
							<FormMessage />
						</FormField>

						<FormErrorList formId={form.id} />
						<StatusButton
							className="w-full"
							status={isPending ? "pending" : form.status ?? "idle"}
							type="submit"
							disabled={isPending}
						>
							Registrarme
						</StatusButton>
					</Form>
				</FormContext>

				{/* <Separator />

				<ul className="flex flex-col gap-5">
					{providerNames.map(providerName => (
						<li key={providerName}>
							<ProviderConnectionForm
								type="Registrarse"
								providerName={providerName}
								redirectTo={redirectTo}
							/>
						</li>
					))}
				</ul> */}
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
